import type { PostHog } from 'posthog-js';
import posthogBrowser from 'posthog-js';

export { posthogBrowser };

const getIsTauri = () => typeof window !== 'undefined' && '__TAURI__' in window;
const TAURI_IPC_CALLBACK_UNPACK_SIGNATURE = '[callbackId, data]';
const TAURI_MISSING_CALLBACK_SIGNATURE = "Couldn't find callback id";

interface CachedAuthSession {
  account?: {
    email?: string;
    label?: string;
  };
}

async function getCachedAuthSession(): Promise<CachedAuthSession | null> {
  if (typeof window === 'undefined' || typeof indexedDB === 'undefined') {
    return null;
  }

  return new Promise(resolve => {
    const timeout = setTimeout(() => resolve(null), 500);

    try {
      const request = indexedDB.open('pique-global-state', 1);

      request.onerror = () => {
        clearTimeout(timeout);
        resolve(null);
      };

      request.onsuccess = () => {
        try {
          const db = request.result;
          if (!db.objectStoreNames.contains('global-state')) {
            clearTimeout(timeout);
            resolve(null);
            return;
          }

          const transaction = db.transaction('global-state', 'readonly');
          const store = transaction.objectStore('global-state');
          const getRequest = store.get('affine-cloud-auth');

          getRequest.onerror = () => {
            clearTimeout(timeout);
            resolve(null);
          };

          getRequest.onsuccess = () => {
            clearTimeout(timeout);
            resolve(getRequest.result as CachedAuthSession | null);
          };
        } catch {
          clearTimeout(timeout);
          resolve(null);
        }
      };
    } catch {
      clearTimeout(timeout);
      resolve(null);
    }
  });
}

async function getTauriInfo() {
  const info: Record<string, string> = {};
  if (!getIsTauri()) return info;
  try {
    const [{ getVersion }, os] = await Promise.all([
      import('@tauri-apps/api/app'),
      import('@tauri-apps/plugin-os'),
    ]);
    info['appVersion'] = await getVersion();
    info['osPlatform'] = os.platform();
    info['osVersion'] = os.version();
    info['osArch'] = os.arch();
  } catch {}
  return info;
}

function sanitizeTelemetryMessage(message?: string | null): string | undefined {
  if (!message) return undefined;
  return message
    .replace(/https?:\/\/\S+/g, '[url]')
    .replace(/Bearer\s+[^\s]+/gi, 'Bearer [redacted]')
    .replace(/[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}/g, '[jwt]')
    .slice(0, 200);
}

function getTauriRuntimeDiagnostics() {
  if (typeof window === 'undefined' || !getIsTauri()) {
    return {};
  }

  const internals = (
    window as typeof window & {
      __TAURI_INTERNALS__?: {
        callbacks?: Map<number, unknown>;
        ipc?: unknown;
        metadata?: {
          currentWindow?: {
            label?: string;
          };
        };
      };
    }
  ).__TAURI_INTERNALS__;

  return {
    tauriHasIpc: Boolean(internals?.ipc),
    tauriCallbackCount:
      internals?.callbacks instanceof Map ? internals.callbacks.size : undefined,
    tauriWindowLabel: internals?.metadata?.currentWindow?.label,
  };
}

function isTauriIpcCallbackError(message: string): boolean {
  return message.includes(TAURI_IPC_CALLBACK_UNPACK_SIGNATURE);
}

function isTauriMissingCallbackError(message: string): boolean {
  return message.includes(TAURI_MISSING_CALLBACK_SIGNATURE);
}

function captureTauriRuntimeDiagnostic(
  ph: PostHog,
  kind: 'callback_unpack' | 'missing_callback',
  message: string,
  extraProperties?: Record<string, unknown>
) {
  ph.capture('tauri_ipc_runtime_error', {
    kind,
    message: sanitizeTelemetryMessage(message),
    pathname: typeof location !== 'undefined' ? location.pathname : undefined,
    visibilityState:
      typeof document !== 'undefined' ? document.visibilityState : undefined,
    ...getTauriRuntimeDiagnostics(),
    ...extraProperties,
  });
}

function createPostHog() {
  let ph: PostHog;

  if (process.env['POSTHOG_KEY']) {
    posthogBrowser.init(process.env['POSTHOG_KEY'], {
      api_host: process.env['POSTHOG_HOST'] || 'https://us.i.posthog.com',
      capture_pageview: false,
      capture_pageleave: false,
      autocapture: false,
      persistence: 'localStorage',
      person_profiles: 'identified_only',
      session_recording: {
        collectFonts: true,
        inlineStylesheet: true,
        recordCrossOriginIframes: true,
        captureCanvas: {
          recordCanvas: true,
          canvasFps: 4,
        },
      },
      enable_recording_console_log: true,
    });
    ph = posthogBrowser;

    const isDevHost =
      typeof location !== 'undefined' &&
      (location.hostname === 'localhost' || location.hostname === '127.0.0.1');

    if (typeof window !== 'undefined' && !isDevHost) {
      const shouldIgnoreError = (message: string): boolean => {
        const ignoredPatterns = [
          'Invalid access: Add Yjs type to a document before reading data',
          'ResizeObserver loop',
          'Non-Error promise rejection',
        ];
        return ignoredPatterns.some(pattern => message.includes(pattern));
      };

      window.addEventListener('error', event => {
        const message = event.message || String(event.error || '');
        if (!message || shouldIgnoreError(message)) {
          return;
        }

        const error =
          event.error instanceof Error
            ? event.error
            : new Error(message || String(event.error));
        const properties = {
          context: 'global_error_handler',
          message: sanitizeTelemetryMessage(message),
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          ...getTauriRuntimeDiagnostics(),
        };

        if (isTauriIpcCallbackError(message)) {
          captureTauriRuntimeDiagnostic(ph, 'callback_unpack', message, {
            source: 'window.error',
          });
        } else if (isTauriMissingCallbackError(message)) {
          captureTauriRuntimeDiagnostic(ph, 'missing_callback', message, {
            source: 'window.error',
          });
        }

        ph.captureException(error, properties);
      });

      window.addEventListener('unhandledrejection', event => {
        const message = event.reason?.message || String(event.reason);
        if (shouldIgnoreError(message)) return;
        const error =
          event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason));
        const properties = {
          context: 'unhandled_promise_rejection',
          message: sanitizeTelemetryMessage(message),
          ...getTauriRuntimeDiagnostics(),
        };

        if (isTauriIpcCallbackError(message)) {
          captureTauriRuntimeDiagnostic(ph, 'callback_unpack', message, {
            source: 'unhandledrejection',
          });
        } else if (isTauriMissingCallbackError(message)) {
          captureTauriRuntimeDiagnostic(ph, 'missing_callback', message, {
            source: 'unhandledrejection',
          });
        }

        ph.captureException(error, properties);
      });
    }
  } else {
    ph = new Proxy(function () {} as unknown as PostHog, {
      get: () =>
        new Proxy(function () {} as unknown as PostHog, {
          get: () => () => {},
          apply: () => {},
        }),
      apply: () => {},
    }) as PostHog;
  }

  return {
    async init() {
      if (!process.env['POSTHOG_KEY']) return;
      const tauriInfo = await getTauriInfo();
      const props: Record<string, unknown> = {
        isDesktop: getIsTauri(),
        platform: getIsTauri() ? 'desktop' : 'web',
        environment: process.env['NODE_ENV'] || 'production',
        ...tauriInfo,
      };
      ph.register(props);

      if (tauriInfo['appVersion']) {
        ph.setPersonProperties({
          appVersion: tauriInfo['appVersion'],
          ...(tauriInfo['osVersion'] && { osVersion: tauriInfo['osVersion'] }),
        });
      }

      try {
        const cachedSession = await getCachedAuthSession();
        if (cachedSession?.account?.email) {
          ph.identify(cachedSession.account.email, {
            email: cachedSession.account.email,
            name: cachedSession.account.label,
            ...(tauriInfo['appVersion'] && {
              appVersion: tauriInfo['appVersion'],
            }),
            ...(tauriInfo['osVersion'] && {
              osVersion: tauriInfo['osVersion'],
            }),
          });
        }
      } catch {}

      ph.capture('app_opened', { trigger: 'cold_start' });
    },

    identify(email: string, properties?: Record<string, unknown>) {
      if (!email) return;
      ph.identify(email, { email, ...properties });
    },

    reset() {
      ph.reset();
    },

    capture(event: string, properties?: Record<string, unknown>) {
      ph.capture(event, properties);
    },

    track(event: string, properties?: Record<string, unknown>) {
      ph.capture(event, properties);
    },

    captureException(error: unknown, properties?: Record<string, unknown>) {
      return ph.captureException(error, properties);
    },

    setPersonProperties(properties: Record<string, unknown>) {
      ph.setPersonProperties(properties);
    },

    setPersonPropertiesOnce(properties: Record<string, unknown>) {
      ph.setPersonProperties(undefined, properties);
    },

    getDistinctId(): string | undefined {
      return ph.get_distinct_id?.();
    },

    opt_out_capturing() {
      ph.opt_out_capturing();
    },

    opt_in_capturing() {
      ph.opt_in_capturing({ captureEventName: false });
    },
  };
}

export const posthog = createPostHog();
posthog.init().catch(() => {});

if (typeof window !== 'undefined') {
  (
    window as unknown as {
      __posthog: {
        identify: typeof posthog.identify;
        reset: typeof posthog.reset;
      };
    }
  ).__posthog = {
    identify: posthog.identify.bind(posthog),
    reset: posthog.reset.bind(posthog),
  };
}
